import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import EventName from "@/components/EventName";
import Brand from "@/components/Brand";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/brand";

// Sponsors page — APPROVED bilingual (Feb 2026).
// Structure/imagery/colours locked; only editorial strings switch by locale.
// Hero overlays strengthened slightly (top-left ivory wash bumped from
// /92 → /98 and /68 → /85) to guarantee dark H1/overline legibility over
// the vehicle image on both English and Arabic. Image and composition
// unchanged.

export default function Sponsors({ locale = "en" }) {
    const isAr = locale === "ar";
    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;
    return (
        <div dir={isAr ? "rtl" : "ltr"} className="min-h-screen bg-[var(--crewz-ivory)] text-[var(--crewz-text-dark)]" data-testid="sponsors-page">
            <Nav />

            {/* §1 HERO */}
            <section
                className="relative h-[60vh] md:h-[65vh] flex items-start overflow-hidden border-b border-[var(--crewz-stone)]"
                data-testid="sponsors-hero"
            >
                <div className="absolute inset-0">
                    <img
                        src={IMAGES.sponsorBg}
                        alt=""
                        role="presentation"
                        className="w-full h-full object-cover opacity-90 kenburns"
                    />
                    {/* Readability overlays — strengthened Feb 2026 (URGENT).
                        Guarantees dark H1 and amber overline first-glance
                        legibility over the vehicle image on both English
                        and Arabic. The top of the section is nearly opaque
                        ivory (matches the page background) and fades
                        cleanly to transparent through the middle so the
                        vehicle image remains visible in the lower half.
                        A soft bottom wash preserves compositional depth. */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[var(--crewz-ivory)] from-0% via-[var(--crewz-ivory)]/92 via-45% to-[var(--crewz-ivory)]/0 to-75%" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--crewz-ivory)]/45" />
                </div>
                <div className="relative w-full px-6 md:px-12 lg:px-20 pt-12 md:pt-16 lg:pt-20 max-w-5xl mx-auto">
                    <p
                        className="overline overline-primary mb-5 text-[var(--crewz-gold)]"
                        style={{
                            textShadow:
                                "0 1px 1px rgba(0,0,0,0.18), 0 0 8px rgba(0,0,0,0.10)",
                        }}
                    >
                        {isAr ? "الشراكة" : "Partnership"}
                    </p>
                    <h1
                        className={`font-display text-5xl md:text-6xl text-[var(--crewz-text-dark)] leading-[0.98] max-w-3xl${isAr ? " !leading-[60px] md:!leading-[72px]" : ""}`}
                        data-testid="sponsors-title"
                    >
                        {isAr ? "أقوى الشراكات تبدأ عندما نبنيها معًا." : "Strong partnerships are built together."}
                    </h1>
                </div>
            </section>

            {/* LEAD PARAGRAPH */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-14 md:pt-20 pb-4 md:pb-6 max-w-5xl mx-auto"
                data-testid="sponsors-lead"
            >
                <p className="font-body text-lg md:text-xl text-[var(--crewz-text-dark)] max-w-3xl font-light leading-relaxed">
                    {isAr ? (
                        <>تبني <Brand /> شراكاتها مع الجهات، والعلامات التجارية، والناشرين، والوجهات، والمؤسسات التي تنسجم رسالتها وأهدافها مع ما تمثله <Brand />. فكل شراكة تُبنى على ما يقدمه كل طرف، وعلى الأثر الإيجابي الذي تتركه في المجتمع.</>
                    ) : (
                        <><Brand /> builds partnerships with organizations, brands, publishers, venues, and institutions whose work sits naturally alongside its own. Every partnership is shaped by what each side brings to it — and by what the community stands to gain from it.</>
                    )}
                </p>
            </section>

            {/* §2 WHAT MAKES A PARTNERSHIP WORTH BUILDING */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-5xl mx-auto"
                data-testid="sponsors-worth-building"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? "متى تستحق الشراكة أن تُبنى؟" : "What makes a partnership worth building"}
                </p>
                <div className="max-w-3xl space-y-6 font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 font-light leading-relaxed">
                    {isAr ? (
                        <>
                            <p>تستحق الشراكة أن تُبنى عندما تحقق قيمةً حقيقيةً لجميع أطرافها: للشريك، ولـ <Brand />، وللمجتمع الذي يجمع بينهما. وقد يكون الظهور الإعلامي، أو الأثر التسويقي، أو النتائج التجارية جزءًا من هذه القيمة، لكنها لا ينبغي أن تكون السبب الوحيد للشراكة. فالشراكة التي تخدم طرفًا واحدًا، أو تقوم على الترويج وحده، لا تدوم.</p>
                            <p>تسعى <Brand /> إلى شراكات تتجاوز مفهوم الترويج. فكل طرف ينبغي أن يقدم قيمةً لا يستطيع الطرف الآخر تحقيقها بمفرده، وأن يخرج المجتمع من هذه الشراكة أكثر قوةً وترابطًا.</p>
                        </>
                    ) : (
                        <>
                            <p>A partnership is worth building when it creates real value on three sides: for the partner, for <Brand />, and for the community that connects them. Exposure, marketing outcomes, or commercial results may be part of that value — but they should not be the whole reason for the relationship. A partnership that serves only one side, or serves only its promotional purpose, does not last.</p>
                            <p><Brand /> looks for partnerships that go further than promotion. Each side should bring something the other could not build alone. And the community should be stronger for the partnership existing at all.</p>
                        </>
                    )}
                </div>
            </section>

            {/* §3 WHAT A PARTNERSHIP CREATES */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-5xl mx-auto border-t border-[var(--crewz-stone)]"
                data-testid="sponsors-creates"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? "الأثر الذي تصنعه الشراكة" : "What a partnership creates"}
                </p>
                <div className="max-w-3xl space-y-6 font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 font-light leading-relaxed">
                    {isAr ? (
                        <>
                            <p>عندما تدخل <Brand /> في شراكة مع جهة أخرى، فإن كلا الطرفين يضع سمعته إلى جانب الآخر. ويظهر كل منهما أمام المجتمع مرتبطًا بالآخر، بما يعكس قرارًا متبادلًا قائمًا على الثقة والاختيار. ولهذا تُبنى الشراكات بعناية، لأنها تمثل علاقة تقوم على المسؤولية والثقة المتبادلة.</p>
                            <p>تحقق الشراكة الناجحة قيمةً لجميع أطرافها. فكل شريك يقدّم ما يميزه، سواء كان ذلك خبرة، أو دعمًا، أو حضورًا، أو موارد، أو مكانة. وفي المقابل، يحصل كل طرف على قيمةٍ متفقٍ عليها منذ البداية. أما المجتمع، فيكون المستفيد الأكبر بما تحققه هذه الشراكة من تجارب، ومبادرات، وفرص لم تكن لتتحقق لولاها.</p>
                            <p>وتختلف مخرجات كل شراكة بحسب طبيعة الاتفاق. فقد تشمل الحضور في الفعاليات، أو الأجنحة، أو المبادرات المعتمدة للعلامة التجارية، أو إنتاج محتوى مشترك، أو التوثيق، أو التفاعل المباشر مع المجتمع من خلال فعاليات وتجارب متفق عليها. وقد تمتد بعض الشراكات لتصبح علاقة طويلة الأمد ضمن مسيرة المجتمع. وفي جميع الأحوال، تُصمم كل شراكة بما يحقق الفائدة للشريك، و<Brand />، والمجتمع.</p>
                        </>
                    ) : (
                        <>
                            <p>When <Brand /> and another organization enter into a partnership, they place their reputations alongside one another. Each side is publicly seen with the other, and each is understood to have chosen the other for a reason. That association is meaningful, and it is why partnerships are entered into carefully.</p>
                            <p>A strong partnership creates value on three sides. Each partner brings something real to the relationship — funding, work, expertise, presence, or standing. Each partner receives something real in return, agreed in advance. And the community — the reason the partnership is possible at all — gains an experience, a piece of work, or a form of presence it would not have had otherwise.</p>
                            <p>The specific outcomes are shaped by each agreement. Some partnerships include event presence, booths, or approved brand activations. Some include joint work, content, or documentation. Some involve meaningful engagement with the community through agreed events, experiences, or activations. Some develop into an ongoing association across the community&apos;s public life. Each partnership is shaped so that the partner, <Brand />, and the community all benefit.</p>
                        </>
                    )}
                </div>
            </section>

            {/* §4 WHAT CREWZNATION EXPECTS */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-5xl mx-auto border-t border-[var(--crewz-stone)]"
                data-testid="sponsors-expects"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? <>توقعات <Brand /> من كل شراكة</> : <>What <Brand /> expects</>}
                </p>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 max-w-3xl font-light leading-relaxed">
                    {isAr ? (
                        <>تعتمد <Brand /> أربعة مبادئ أساسية في جميع شراكاتها، تنطبق سواء كانت الشراكة مرتبطة بنسخة واحدة من <EventName surface="light" /> أو امتدت عبر سنوات.</>
                    ) : (
                        <><Brand /> asks four things of every partnership. They apply whether the partnership centres on a single edition of <EventName surface="light" /> or develops across many years.</>
                    )}
                </p>
                <ol className="mt-10 space-y-7 max-w-3xl list-none" data-testid="sponsors-expectations">
                    <li>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? "توافق المعايير" : "Aligned standards."}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>ينبغي أن تنسجم معايير الشريك وسلوكه المهني مع المعايير التي تلتزم بها <Brand />.</>
                            ) : (
                                <>The partner&apos;s work and conduct should align with the standards <Brand /> keeps for itself.</>
                            )}
                        </p>
                    </li>
                    <li>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? "إسهام حقيقي في المجتمع" : "A real contribution to the community."}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>ينبغي أن تقدم كل شراكة قيمةً حقيقية للمجتمع، بما يتناسب مع الحضور والثقة التي تحظى بها من خلال <Brand />.</>
                            ) : (
                                <>Every partnership should give the community something meaningful in return for the presence and attention it receives.</>
                            )}
                        </p>
                    </li>
                    <li>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? "الاستخدام المتفق عليه للأسماء والهوية" : "Agreed use of names and identity."}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>يُتفق مسبقًا على أي استخدامٍ عام للأسماء، أو الشعارات، أو الهوية البصرية، أو الحضور المجتمعي، سواء من جانب <Brand /> أو من جانب الشريك.</>
                            ) : (
                                <>Any public use of names, logos, imagery, or community presence — by either side — is agreed in advance between both organizations.</>
                            )}
                        </p>
                    </li>
                    <li>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? "وضوح الأهداف والنتائج" : "Clarity on purpose and outcomes."}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>يتفق الطرفان منذ البداية على هدف الشراكة، ومسؤوليات كل طرف، ومعايير نجاحها بالنسبة للشريك، و<Brand />، والمجتمع.</>
                            ) : (
                                <>Both sides agree on what the partnership is for, what each side will contribute, and what success looks like for the partner, for <Brand />, and for the community.</>
                            )}
                        </p>
                    </li>
                </ol>
                <p className="font-body text-sm md:text-base text-[var(--crewz-text-dark)]/60 mt-10 max-w-3xl leading-relaxed">
                    {isAr
                        ? "تبدأ هذه المبادئ منذ أول اجتماع، وتظل الإطار الذي يحكم الشراكة طوال فترة استمرارها."
                        : "These expectations hold from the first conversation onward, and remain in place for as long as the partnership continues."}
                </p>
            </section>

            {/* §5 WHAT CREWZNATION DOES NOT DO */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-5xl mx-auto border-t border-[var(--crewz-stone)]"
                data-testid="sponsors-boundaries"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? <>ما لا تقبله <Brand /></> : <>What <Brand /> does not do</>}
                </p>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 max-w-3xl font-light leading-relaxed">
                    {isAr
                        ? "تلتزم جميع الشراكات بثلاثة ضوابط أساسية، بصرف النظر عن حجمها، أو ميزانيتها، أو نطاقها."
                        : "Three boundaries apply to every partnership, regardless of scale, budget, or scope."}
                </p>
                <div className="mt-10 space-y-8 max-w-3xl">
                    <div>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? "خصوصية الأعضاء محفوظة" : "Member information is protected."}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>لا تُشارك البيانات الشخصية أو وسائل التواصل الخاصة بأعضاء <Brand /> مع أي شريك لأغراض تسويقية غير متفق عليها. وعند رغبة أحد الشركاء في التواصل مع الأعضاء، يتم ذلك من خلال فعاليات أو تجارب أو مبادرات يتم الاتفاق عليها مسبقًا.</>
                            ) : (
                                <>Personal contact information belonging to <Brand />&apos;s members is not provided to partners for unsolicited marketing. Where a partner wishes to engage with members, the engagement happens through agreed events, experiences, or activations.</>
                            )}
                        </p>
                    </div>
                    <div>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? (
                                <>استخدام اسم وهوية <Brand /> يتطلب موافقة مسبقة</>
                            ) : (
                                <><Brand />&apos;s name and identity require approval.</>
                            )}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>لا يجوز استخدام اسم <Brand />، أو شعارها، أو هويتها البصرية، أو حضورها المجتمعي خارج نطاق ما تم الاتفاق عليه بين الطرفين. ويخضع أي استخدامٍ عام لموافقةٍ مسبقة.</>
                            ) : (
                                <>A partner does not use <Brand />&apos;s name, logo, imagery, or community presence outside the scope agreed between both sides. Each public use is confirmed in advance.</>
                            )}
                        </p>
                    </div>
                    <div>
                        <p className="font-display text-xl md:text-2xl text-[var(--crewz-text-dark)] leading-snug">
                            {isAr ? "معايير السلامة والمجتمع غير قابلة للتفاوض" : "Safety and community standards are not negotiable."}
                        </p>
                        <p className="font-body text-base md:text-lg text-[var(--crewz-text-dark)]/80 mt-2 font-light leading-relaxed">
                            {isAr ? (
                                <>لا يبرر أي هدف تجاري، مهما بلغت أهميته، تجاوز معايير <Brand /> في السلوك، والسلامة، وطبيعة فعالياتها. وتلتزم جميع الشراكات والمبادرات بهذه المعايير دون استثناء.</>
                            ) : (
                                <>No commercial objective — however significant — overrides <Brand />&apos;s standards of conduct, safety, and the character of its gatherings. These standards apply equally to every partner and every activation.</>
                            )}
                        </p>
                    </div>
                </div>
            </section>

            {/* §6 HOW PARTNERSHIPS DEVELOP */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-5xl mx-auto border-t border-[var(--crewz-stone)]"
                data-testid="sponsors-develop"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? "كيف تتطور الشراكات" : "How partnerships develop"}
                </p>
                <div className="max-w-3xl space-y-6 font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 font-light leading-relaxed">
                    {isAr ? (
                        <>
                            <p>تبدأ الشراكة عادةً بحوار، سواء كان حضوريًا أو مكتوبًا، يتناول طبيعة عمل كل طرف، ومدى توافق الرؤية بينهما. ومن هناك، يحدد الطرفان شكل الشراكة عمليًا، وما سيقدمه كل منهما، وكيف سيُقاس نجاحها بالنسبة للشريك، و<Brand />، والمجتمع.</p>
                            <p>تُراجع كل فرصة بعناية قبل الالتزام بها. ويعتمد مستوى هذه المراجعة على حجم الشراكة وطبيعتها؛ فالمقترحات الواضحة التي تستوفي معايير <Brand /> قد تمضي بوتيرة أسرع، بينما تتطلب الشراكات الأكثر تعقيدًا أو خصوصية وقتًا أطول. وفي جميع الأحوال، تحدد طبيعة الشراكة الإطار الزمني المناسب لها.</p>
                            <p>تتنوع أشكال الشراكات بحسب أهدافها. فقد تقتصر على نسخة واحدة من <EventName surface="light" /> أو إحدى الفعاليات، وقد تتمحور حول المحتوى أو العمل التحريري، وقد تمتد عبر فعاليات متعددة ولسنوات. وفي جميع الحالات، يُتفق على شكل الشراكة بما يعكس أهدافها والغاية التي أُنشئت من أجلها.</p>
                        </>
                    ) : (
                        <>
                            <p>A partnership typically begins with a conversation — in person or in writing — about what each side does and where alignment might exist. From there, both sides define what the partnership will look like in practice, what each will contribute, and what success will look like on all three sides.</p>
                            <p>Every opportunity is reviewed before commitment. The depth of that review depends on the scale and nature of the partnership: a well-defined proposal that already meets <Brand />&apos;s standards may move efficiently; a more complex or bespoke arrangement takes longer. In either case, the pace is set by what the relationship requires.</p>
                            <p>Partnerships take many shapes. Some centre on a single Gathering or event. Some develop as an editorial or content relationship. Some continue across events and years. The shape a partnership takes is agreed together, and reflects what the relationship is actually for.</p>
                        </>
                    )}
                </div>
            </section>

            {/* §7 A NOTE ON NAMED PARTNERS */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-5xl mx-auto border-t border-[var(--crewz-stone)]"
                data-testid="sponsors-named-note"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? "ملاحظة بشأن الشركاء المذكورين" : "A note on named partners"}
                </p>
                <p className="max-w-3xl font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 font-light leading-relaxed">
                    {isAr
                        ? "لا يُذكر أي شريك في هذه الصفحة إلا عند وجود شراكةٍ قائمة وموثقة يمكن عرضها إلى جانب الأعمال أو المبادرات المرتبطة بها. ولا تُنشر هنا شعارات مؤقتة، أو قوائم للشركاء المحتملين، أو إعلانات \"قريبًا\"."
                        : "Partners are named on this page only when a documented, active partnership can be presented alongside the work it involves. Placeholder logos, prospect lists, and \u201Ccoming soon\u201D announcements are not published here."}
                </p>
            </section>

            {/* §8 BEGIN THE CONVERSATION */}
            <section
                className="px-6 md:px-12 lg:px-20 py-20 md:py-28 max-w-5xl mx-auto border-t border-[var(--crewz-stone)]"
                data-testid="sponsors-invitation"
            >
                <p className="overline overline-primary mb-5 text-[var(--crewz-gold)]">
                    {isAr ? "لنبدأ الحوار" : "Begin the conversation"}
                </p>
                <p className="max-w-3xl font-body text-base md:text-lg text-[var(--crewz-text-dark)]/85 font-light leading-relaxed">
                    {isAr ? (
                        <>إذا كانت جهتك تدرس بناء شراكة مع <Brand />، فإن البداية تكون بحوار. تكفي رسالة مختصرة تعرّف بجهتكم، وتوضح رؤيتكم للشراكة المقترحة، لبدء النقاش. ولا توجد صيغة محددة للمقترحات، أو نموذج يتعين تعبئته.</>
                    ) : (
                        <>If your organization is considering a partnership with <Brand />, the first step is a conversation. A short note describing your organization and how you see the relationship is enough to begin. There is no proposal template and no form to complete.</>
                    )}
                </p>
                <Link
                    to={isAr ? "/ar/contact?reason=brand" : "/contact?reason=brand"}
                    className="inline-flex items-center gap-2 mt-10 border border-[var(--crewz-gold)] text-[var(--crewz-text-dark)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-ivory)] px-8 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors"
                    data-testid="sponsors-cta-begin"
                >
                    {isAr ? "ابدأ الحوار" : "Begin the conversation"}
                </Link>

                {/* Institutional partner marks — placed directly beneath
                    the CTA so first-time visitors and prospective
                    sponsors read them as an intentional part of the
                    invitation. Two marks share the same rail:
                      • Siyak Alruyah Alalamiyah
                      • INCAS (current CrewZNatioN sponsor)
                    Logos are never mirrored on Arabic routes; the row
                    is `dir="ltr"` so slot order is preserved in both
                    languages. */}
                <div
                    className="mt-14 md:mt-16 flex flex-wrap md:flex-nowrap items-center justify-start gap-x-8 gap-y-8 md:gap-x-10"
                    dir="ltr"
                    data-testid={isAr ? "sponsors-partner-marks-ar" : "sponsors-partner-marks"}
                >
                    <a
                        href="https://siyak.sa/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={isAr
                            ? "سياق الرؤية العالمية — فتح siyak.sa في تبويب جديد"
                            : "Siyak Alruyah Alalamiyah — opens siyak.sa in a new tab"}
                        title={isAr
                            ? "سياق الرؤية العالمية"
                            : "Siyak Alruyah Alalamiyah"}
                        className="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--crewz-gold)] focus-visible:ring-offset-2 rounded-sm"
                        data-testid={isAr ? "sponsors-siyak-link-ar" : "sponsors-siyak-link"}
                    >
                        <img
                            src="/assets/partners/siyak-alruyah-alalamiyah-sponsors.png"
                            alt={isAr
                                ? "شعار سياق الرؤية العالمية"
                                : "Siyak Alruyah Alalamiyah logo"}
                            className="h-32 md:h-40 w-auto select-none"
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                        />
                    </a>

                    <a
                        href="https://www.incas.sa/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={isAr
                            ? "INCAS — فتح incas.sa في تبويب جديد"
                            : "INCAS — opens incas.sa in a new tab"}
                        title="INCAS"
                        className="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--crewz-gold)] focus-visible:ring-offset-2 rounded-sm"
                        data-testid={isAr ? "sponsors-incas-link-ar" : "sponsors-incas-link"}
                    >
                        <img
                            src="/logos/incas-dark.png"
                            alt="INCAS"
                            className="h-[100px] md:h-[140px] w-auto select-none"
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                        />
                    </a>

                    <a
                        href="https://www.instagram.com/thefahrers"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={isAr
                            ? "The Fahrers — فتح إنستغرام في تبويب جديد"
                            : "The Fahrers — opens Instagram in a new tab"}
                        title="The Fahrers"
                        className="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--crewz-gold)] focus-visible:ring-offset-2 rounded-sm"
                        data-testid={isAr ? "sponsors-thefahrers-link-ar" : "sponsors-thefahrers-link"}
                    >
                        <img
                            src="/logos/thefahrers-dark.png"
                            alt="The Fahrers"
                            className="h-[38px] md:h-[50px] w-auto select-none"
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                        />
                    </a>
                </div>
            </section>

            <PageFooter />
        </div>
    );
}
